import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft, ShieldAlert, Loader2, Zap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LeadsList } from "@/components/admin/LeadsList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const GoogleG = () => (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.03c2.2-2 3.5-5 3.5-8.6z" />
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.14.01-3 2.3-.04.13C4.1 20.8 7.8 24 12 24z" />
        <path fill="#FBBC05" d="M5.2 14.4c-.25-.7-.4-1.5-.4-2.4s.15-1.7.42-2.4l-.01-.16-3.1-2.4-.1.07A11.9 11.9 0 0 0 0 12c0 1.9.46 3.7 1.28 5.3l3.92-2.9z" />
        <path fill="#EA4335" d="M12 4.6c2.2 0 3.7.9 4.5 1.7l3.3-3.2C17.9 1.2 15.2 0 12 0 7.8 0 4.1 2.2 2.1 5.7l3.9 3c1-2.9 3.7-4.1 6-4.1z" />
    </svg>
);

const copy = {
    en: {
        loginHint: "Owner access — leads inbox",
        login: "Sign in with Google",
        checking: "Checking your session...",
        title: "Leads inbox",
        sub: "Every quote request, newest first.",
        forbidden: "This Google account isn't the owner account. Sign in with kennet.ramirez91@gmail.com.",
        error: "Couldn't load leads.",
        retry: "Retry",
        logout: "Sign out",
        back: "Back to site",
        authError: "Google sign-in didn't complete. Please try again.",
    },
    es: {
        loginHint: "Acceso del propietario — bandeja de leads",
        login: "Iniciar sesión con Google",
        checking: "Verificando tu sesión...",
        title: "Bandeja de leads",
        sub: "Todas las solicitudes de cotización, más recientes primero.",
        forbidden: "Esta cuenta de Google no es la del propietario. Inicia sesión con kennet.ramirez91@gmail.com.",
        error: "No se pudieron cargar los leads.",
        retry: "Reintentar",
        logout: "Salir",
        back: "Volver al sitio",
        authError: "El inicio de sesión con Google no se completó. Intenta de nuevo.",
    },
};

export const AdminPage = () => {
    const { lang } = useLang();
    const c = copy[lang] || copy.en;
    const location = useLocation();
    const navigate = useNavigate();

    const [auth, setAuth] = useState(location.state?.user ? true : null);
    const [leads, setLeads] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [authError, setAuthError] = useState(Boolean(location.state?.authError));

    useEffect(() => {
        if (location.state?.user) return;
        let live = true;
        (async () => {
            try {
                const r = await fetch(`${API}/auth/me`, { credentials: "include" });
                if (!r.ok) throw new Error();
                await r.json();
                if (!live) return;
                setAuth(true);
            } catch {
                if (live) setAuth(false);
            }
        })();
        return () => {
            live = false;
        };
    }, [location.state]);

    useEffect(() => {
        if (!auth) return;
        let live = true;
        (async () => {
            try {
                const r = await fetch(`${API}/leads`, { credentials: "include" });
                if (r.status === 403) {
                    if (live) {
                        setLeads([]);
                        setLoadError("forbidden");
                    }
                    return;
                }
                if (!r.ok) throw new Error();
                const d = await r.json();
                if (live) {
                    setLeads(d.leads);
                    setLoadError(false);
                }
            } catch {
                if (live) {
                    setLeads([]);
                    setLoadError("load");
                }
            }
        })();
        return () => {
            live = false;
        };
    }, [auth]);

    const logout = async () => {
        try {
            await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
        } catch (e) {
            /* clear locally regardless */
        }
        navigate("/", { replace: true });
    };

    if (auth === null) {
        return (
            <div data-testid="admin-loading" className="grid min-h-screen place-items-center bg-obsidian">
                <span className="flex items-center gap-3 text-sm font-bold text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin text-volt" />
                    {c.checking}
                </span>
            </div>
        );
    }

    if (auth === false) {
        return (
            <div data-testid="admin-login" className="grid min-h-screen place-items-center bg-obsidian px-4">
                <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-surface p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-volt shadow-[0_0_30px_rgba(212,255,0,0.35)]">
                        <Zap className="h-7 w-7 text-obsidian" />
                    </span>
                    <h1 className="mt-5 font-display text-2xl font-black text-white">{c.title}</h1>
                    <p className="mt-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">{c.loginHint}</p>
                    {authError && (
                        <p data-testid="admin-auth-error" className="mt-4 rounded-xl border border-blaze/40 bg-blaze/10 px-3 py-2 text-xs font-bold text-blaze">
                            {c.authError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={googleLogin}
                        data-testid="admin-login-btn"
                        className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-white px-6 py-3.5 text-sm font-extrabold text-slate-800 transition-all hover:bg-slate-200 active:scale-[0.98]"
                    >
                        <GoogleG />
                        {c.login}
                    </button>
                    <a
                        href="/"
                        data-testid="admin-back-link"
                        className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 transition-colors hover:text-volt"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        {c.back}
                    </a>
                </div>
            </div>
        );
    }

    if (loadError === "forbidden") {
        return (
            <div data-testid="admin-forbidden" className="grid min-h-screen place-items-center bg-obsidian px-4">
                <div className="w-full max-w-sm rounded-3xl border border-blaze/40 bg-blaze/10 p-8 text-center">
                    <ShieldAlert className="mx-auto h-10 w-10 text-blaze" />
                    <p className="mt-4 text-sm font-bold leading-relaxed text-white">{c.forbidden}</p>
                    <button
                        type="button"
                        onClick={logout}
                        data-testid="admin-forbidden-logout-btn"
                        className="mt-6 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:border-volt/50 hover:text-volt"
                    >
                        <LogOut className="h-4 w-4" />
                        {c.logout}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-page" className="min-h-screen bg-obsidian font-body text-slate-100 antialiased">
            <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                    <div>
                        <span className="inline-flex items-center gap-2 rounded-full border border-volt/30 bg-volt/10 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-volt">
                            <Zap className="h-3 w-3" />
                            Admin
                        </span>
                        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">{c.title}</h1>
                        <p className="mt-1 text-sm text-slate-400">{c.sub}</p>
                    </div>
                    <div className="flex items-center gap-2.5">
                        <a
                            href="/"
                            data-testid="admin-back-site-btn"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {c.back}
                        </a>
                        <button
                            type="button"
                            onClick={logout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-bold text-slate-300 transition-colors hover:border-blaze/50 hover:text-blaze"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            {c.logout}
                        </button>
                    </div>
                </div>

                <div className="mt-8">
                    <LeadsList
                        leads={leads}
                        loading={leads === null}
                        error={loadError === "load"}
                        onRetry={() => window.location.reload()}
                        errorText={c.error}
                        retryText={c.retry}
                        emptyText={c.empty}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminPage;
